// Shared state of the app and loading from the API. Views read S and call render() after changes.
import {
  ApiError,
  type Activity,
  type AngeBotState,
  type Api,
  type Briefing,
  type ChangeRequest,
  type ClaudeChat,
  type ClaudeChatSummary,
  type ClaudeDocSummary,
  type CompanyDetail,
  type CompanySummary,
  type ContactOption,
  type Deal,
  type DuplicateGroup,
  type DealMail,
  type LogEntry,
  type Inquiry,
  type Mail,
  type MailActions,
  type Me,
  type Meta,
  type Stage,
  type Task,
} from "./api";
import type { Requirements } from "@pipeline/core";
import type { Auth } from "./auth";
import { toast } from "./util";

export type View = "briefing" | "anfragen" | "board" | "volumen" | "klaerfaelle" | "firmen" | "firma" | "claude" | "einstellungen" | "antraege" | "aufgaben" | "protokoll";
export const VIEWS: readonly View[] = ["briefing", "anfragen", "board", "volumen", "klaerfaelle", "firmen", "claude", "einstellungen", "antraege", "aufgaben", "protokoll"];

export const S = {
  api: null as unknown as Api,
  auth: null as unknown as Auth,
  me: null as Me | null,
  /** Spec 10: write controls are disabled for Vertrieb.Read. */
  readOnly: true,
  loaded: false,
  view: "briefing" as View,
  meta: null as Meta | null,
  briefing: null as Briefing | null,
  deals: new Map<string, Deal>(),
  unclear: [] as Mail[],
  /** Graded inquiries (open, or with the finished ones when inqDone) and inquiry mails still waiting for grading. */
  inquiries: [] as Inquiry[],
  pendingInquiries: [] as Mail[],
  inqDone: false,
  inqGrade: "alle" as "alle" | "A" | "B" | "C",
  /** Inquiries picked for the bulk release. */
  inqPicked: new Set<string>(),
  mailActions: { send: "off", drafts: false, infoMove: false, ownMailbox: false, sendOnly: false } as MailActions,
  users: [] as { id: string; name: string }[],
  openId: null as string | null,
  /** The open deal as loaded for the drawer, also one beyond the 500 of the list (which it does not join). */
  openDeal: null as Deal | null,
  dealMails: [] as DealMail[],
  dealActivities: [] as Activity[],
  /** The company's contacts the open deal's pickers offer (not hidden, not yet at the deal). */
  dealContactOptions: [] as ContactOption[],
  /** Open tasks of the open deal; the first is its next step. */
  dealTasks: [] as Task[],
  /** "Meine Aufgaben" (view "aufgaben"): mine or all open ones; null: not loaded. */
  tasks: null as Task[] | null,
  tasksScope: "mine" as "mine" | "all",
  /** Required fields for Ange.Bot of the open deal (spec 8a). */
  dealRequirements: null as Requirements | null,
  dealRequirementsAt: null as string | null,
  /** Hand-over to Ange.Bot of the open deal (M9). */
  dealAngeBot: null as AngeBotState | null,
  /** Claude's documents kept at the open deal. */
  dealDocuments: [] as ClaudeDocSummary[],
  /** The global Claude (view "claude", #claude-<id>). */
  claudeChats: null as ClaudeChatSummary[] | null,
  claudeChatId: null as string | null,
  claudeChat: null as ClaudeChat | null,
  /** Company list (view "firmen") and the open company page (view "firma", #firma-<id>). */
  companies: null as CompanySummary[] | null,
  companyQuery: "",
  allCompanies: false,
  companyId: null as string | null,
  company: null as CompanyDetail | null,
  /** Possible duplicates for admins on the company list (AB3); null: not loaded. */
  duplicates: null as DuplicateGroup[] | null,
  /** Open change requests of customers (view "antraege", AB4); null: not loaded. */
  changeRequests: null as ChangeRequest[] | null,
  /** Pushes to Ange.Bot and feasibility checks (view "protokoll"), newest first; null: not loaded. */
  angeBotLog: null as LogEntry[] | null,
  /** The log has older entries than the loaded ones. */
  angeBotLogMore: false,
  q: "",
  showClosed: false,
  reviewOnly: false,
};

export const stages = (): Stage[] => S.meta?.stages ?? [];
export const stageLabel = (id: string) => stages().find((s) => s.id === id)?.label ?? id;
export const isClosed = (id: string) => !!stages().find((s) => s.id === id)?.closed;
export const openDeals = () => [...S.deals.values()].filter((d) => !isClosed(d.stage));
/** The deal in the drawer: from the list, or as loaded for the drawer when the list does not have it. */
export const drawerDeal = (): Deal | undefined =>
  S.openId ? (S.deals.get(S.openId) ?? (S.openDeal?.id === S.openId ? S.openDeal : undefined)) : undefined;

/** Everything the views need; the open deal's mails separately. */
export async function loadAll(): Promise<void> {
  const [meta, briefing, deals, unclear, pending, inquiries, users, mailActions] = await Promise.all([
    S.api.meta(),
    S.api.briefing(),
    S.api.deals(true),
    S.api.mails("unklar"),
    S.api.mails("anfrage"),
    S.api.inquiries(S.inqDone),
    S.api.users(),
    // Not worth failing the whole view for: without it nothing is sent.
    S.api.mailActions().catch((): MailActions => ({ send: "off", drafts: false, infoMove: false, ownMailbox: false, sendOnly: false })),
  ]);
  S.mailActions = mailActions;
  S.meta = meta;
  S.briefing = briefing;
  S.deals = new Map(deals.map((d) => [d.id, d]));
  S.unclear = unclear;
  S.inquiries = inquiries;
  S.pendingInquiries = pending;
  S.users = users;
  S.loaded = true;
  if (S.openId) await loadDealDetail();
  if (S.view === "firma") await loadCompany();
  if (S.view === "antraege") await loadChangeRequests();
  if (S.view === "aufgaben") await loadTasks();
  if (S.view === "protokoll") await loadLog();
}

/** A page of the log, as the API hands it out. */
const LOG_PAGE = 50;

/**
 * The log (view "protokoll"): the newest entries, or with `older` the page after the loaded ones. A refresh renews
 * the newest and keeps older pages loaded on request.
 */
export async function loadLog(older = false): Promise<void> {
  const cur = S.angeBotLog ?? [];
  const items = await S.api.angeBotLog(older ? cur.at(-1)?.at : undefined);
  const last = items.at(-1)?.at;
  if (older || !S.angeBotLog) S.angeBotLogMore = items.length === LOG_PAGE;
  S.angeBotLog = older ? [...cur, ...items] : [...items, ...(items.length === LOG_PAGE && last ? cur.filter((e) => e.at < last) : [])];
}

export async function loadTasks(): Promise<void> {
  const scope = S.tasksScope;
  const tasks = await S.api.tasks(scope);
  // The filter was switched meanwhile: its own load sets the list.
  if (S.tasksScope === scope) S.tasks = tasks;
}

export async function loadChangeRequests(): Promise<void> {
  S.changeRequests = await S.api.changeRequests();
}

/** Open inquiries: graded and still new, plus the ones waiting for their grading. */
export const openInquiries = () => S.inquiries.filter((i) => i.status === "neu").length + S.pendingInquiries.length;

/** The hash of the current view, e.g. to return there from the deal drawer. */
export const viewHash = () =>
  S.view === "firma" && S.companyId
    ? `firma-${S.companyId}`
    : S.view === "claude" && S.claudeChatId
      ? `claude-${S.claudeChatId}`
      : S.view === "klaerfaelle"
        ? "zuordnen"
        : S.view;

export async function loadCompanies(): Promise<void> {
  S.companies = await S.api.companies(S.companyQuery.trim(), S.allCompanies);
}

/** For admins only; a failure leaves the list without the panel. */
export async function loadDuplicates(): Promise<void> {
  S.duplicates = S.me?.isAdmin ? await S.api.companyDuplicates().catch(() => []) : [];
}

export async function loadCompany(): Promise<void> {
  const id = S.companyId;
  if (!id) return;
  const company = await S.api.company(id);
  // Another company was opened meanwhile: its own load sets it.
  if (S.companyId === id) S.company = company;
}

/** Mails and activities of the open deal. */
export async function loadDealDetail(): Promise<void> {
  const id = S.openId;
  if (!id) return;
  const [mails, detail, angeBot, documents] = await Promise.all([
    S.api.dealMails(id),
    S.api.deal(id),
    S.api.angeBot(id).catch(() => null),
    S.api.dealDocuments(id).catch(() => []),
  ]);
  // A late answer for a deal that is no longer open must not fill the drawer of the next one.
  if (S.openId !== id) return;
  S.dealAngeBot = angeBot;
  S.dealDocuments = documents;
  S.dealMails = mails;
  const { activities, tasks, requirements, requirementsAt, contactOptions, ...deal } = detail;
  // Not into S.deals: the board, its counts and the briefing stay with the list as loaded.
  S.openDeal = deal;
  S.dealActivities = activities;
  S.dealContactOptions = contactOptions;
  S.dealTasks = tasks;
  S.dealRequirements = requirements;
  S.dealRequirementsAt = requirementsAt;
}

let renderAll: () => void = () => undefined;
export function setRenderer(fn: () => void): void {
  renderAll = fn;
}
export function render(): void {
  renderAll();
}

/**
 * Runs a change and reloads. 409: someone else changed the record, so reload and say so. 403: read-only access.
 */
export async function write(fn: () => Promise<unknown>, okMsg?: string): Promise<boolean> {
  if (S.readOnly) return false;
  try {
    await fn();
    if (okMsg) toast(okMsg);
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.code === "conflict") toast("Inzwischen geändert. Die Ansicht ist neu geladen, bitte noch einmal.");
    else if (err instanceof ApiError && err.status === 403) {
      S.readOnly = true;
      toast("Für dich ist die Pipeline nur lesbar.");
    } else toast(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen. Bitte erneut versuchen.");
    return false;
  } finally {
    await loadAll().catch(() => undefined);
    render();
  }
}
